import { WithApiRequestMeta } from "../../api-results";
import {
  PaginatedRequestParams,
  PaginatedResponse,
  RtkEmptySplitApi,
} from "../../types";
import type {
  ClientContact,
  CreateContactPayload,
  UpdateClientContactPayload,
} from "./types";

export const addContactsApi = (emptySplitApi: RtkEmptySplitApi) => {
  const contactsApi = emptySplitApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
      listContactFields: build.query<
        string[],
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/contacts/list_fields`,
          method: "GET",
        }),
      }),
      contactsList: build.query<
        PaginatedResponse<ClientContact>,
        WithApiRequestMeta<
          PaginatedRequestParams<{ entityId: string; searchTerm?: string }>
        >
      >({
        query: ({ entityId, page, page_size, searchTerm }) => ({
          url: `/api/auth/${entityId}/contacts/list`,
          method: "POST",
          params: {
            search_term: searchTerm ? `%${searchTerm}%` : undefined,
          },
          data: {
            page: page || 1,
            page_size: page_size || 20,
          },
        }),
      }),
      getContactByConversationId: build.query<
        Partial<ClientContact>,
        WithApiRequestMeta<{ entityId: string; conversationId: string }>
      >({
        query: ({ entityId, conversationId }) => ({
          url: `/api/auth/${entityId}/contacts/${conversationId}`,
          method: "GET",
        }),
      }),
      getContactByContactId: build.query<
        Partial<ClientContact>,
        WithApiRequestMeta<{ entityId: string; contactId: string }>
      >({
        query: ({ entityId, contactId }) => ({
          url: `/api/auth/${entityId}/contacts`,
          method: "GET",
          params: {
            contact_id: contactId,
          },
        }),
      }),
      createContact: build.mutation<
        ClientContact,
        WithApiRequestMeta<{ entityId: string; data: CreateContactPayload }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/contacts`,
          method: "POST",
          data,
        }),
      }),
      updateContact: build.mutation<
        ClientContact,
        WithApiRequestMeta<
          PaginatedRequestParams<{
            entityId: string;
            contactId: string;
            data: UpdateClientContactPayload;
            searchTerm?: string;
          }>
        >
      >({
        query: ({ entityId, contactId, data }) => ({
          url: `/api/auth/${entityId}/contacts/${contactId}`,
          method: "PUT",
          data,
        }),
        onQueryStarted: async (
          { entityId, contactId, data, page, page_size, searchTerm },
          { dispatch, queryFulfilled }
        ) => {
          const listQueryArgs = {
            entityId,
            page,
            page_size,
            searchTerm,
          };

          const patchResult = dispatch(
            contactsApi.util.updateQueryData(
              "contactsList",
              listQueryArgs,
              (draft) => {
                const contactToUpdate = draft.data.find(
                  (contact) => contact.id === contactId
                );

                if (!contactToUpdate) {
                  return;
                }

                Object.assign(contactToUpdate, data);
              }
            )
          );

          try {
            const { data: updatedContact } = await queryFulfilled;

            dispatch(
              contactsApi.util.updateQueryData(
                "contactsList",
                listQueryArgs,
                (draft) => {
                  const contactIndex = draft.data.findIndex(
                    (contact) => contact.id === contactId
                  );

                  if (contactIndex === -1) {
                    return;
                  }

                  draft.data[contactIndex] = {
                    ...draft.data[contactIndex],
                    ...updatedContact,
                  };
                }
              )
            );
          } catch {
            patchResult.undo();
          }
        },
      }),
    }),
  });

  return contactsApi;
};

export type ContactsApi = ReturnType<typeof addContactsApi>;
